<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">聯絡詢問紀錄</h1>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋姓名、公司、電話或 Email…"
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="selectedType" class="form-control" @change="handleFilterChange">
          <option value="">全部需求類型</option>
          <option value="demo">預約 Demo</option>
          <option value="quote">正式報價</option>
          <option value="trial">免費試用</option>
          <option value="partnership">合作洽談</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="selectedStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部處理狀態</option>
          <option value="new">新進</option>
          <option value="in_progress">處理中</option>
          <option value="done">已完成</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner" /><p>載入中...</p></div>
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchInquiries">重試</button>
    </div>
    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>時間</th>
            <th>姓名</th>
            <th>公司</th>
            <th>電話</th>
            <th>Email</th>
            <th>類型</th>
            <th>狀態</th>
            <th>說明</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="inquiries.length === 0">
            <td colspan="9" class="empty-state">尚無聯絡詢問紀錄</td>
          </tr>
          <tr v-for="item in inquiries" :key="item.id">
            <td class="col-time">{{ formatDate(item.created_at) }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.company }}</td>
            <td>{{ item.phone }}</td>
            <td>{{ item.email || '—' }}</td>
            <td><span class="badge">{{ typeLabel(item.type) }}</span></td>
            <td><span class="badge" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span></td>
            <td class="col-message">{{ item.message || '—' }}</td>
            <td class="col-actions">
              <button class="btn-action" title="處理" @click="openEdit(item)"><i class="ri-edit-line"></i></button>
              <button class="btn-action danger" title="刪除" @click="remove(item)"><i class="ri-delete-bin-line"></i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="editing" class="modal-backdrop" @click.self="editing = null">
      <div class="modal-panel">
        <h2>{{ editing.company }} — {{ editing.name }}</h2>
        <p class="meta">{{ editing.phone }} · {{ editing.email || '無 Email' }}</p>
        <div class="form-group">
          <label>處理狀態</label>
          <select v-model="editForm.status" class="form-control">
            <option value="new">新進</option>
            <option value="in_progress">處理中</option>
            <option value="done">已完成</option>
          </select>
        </div>
        <div class="form-group">
          <label>備註</label>
          <textarea v-model="editForm.adminNote" class="form-control" rows="4" />
        </div>
        <div class="form-group">
          <label>需求說明</label>
          <p class="readonly">{{ editing.message || '—' }}</p>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="editing = null">關閉</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveEdit">{{ saving ? '儲存中…' : '儲存' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { websiteContactService } from '@/api/modules/website.js'
import { showSuccess, showError, showConfirm } from '@/utils/swal.js'

const TYPE_LABELS = { demo: '預約 Demo', trial: '免費試用', quote: '正式報價', partnership: '合作洽談' }

export default {
  name: 'WebsiteContactInquiriesIndex',
  data() {
    return {
      inquiries: [],
      loading: false,
      error: '',
      searchQuery: '',
      selectedType: '',
      selectedStatus: '',
      searchTimer: null,
      editing: null,
      editForm: { status: 'new', adminNote: '' },
      saving: false,
    }
  },
  mounted() {
    this.fetchInquiries()
  },
  methods: {
    typeLabel(t) { return TYPE_LABELS[t] || t },
    statusLabel(s) {
      return ({ new: '新進', in_progress: '處理中', done: '已完成' })[s] || s || '新進'
    },
    statusClass(s) {
      return ({ new: 'badge-warning', in_progress: 'badge-info', done: 'badge-success' })[s] || ''
    },
    formatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('zh-TW')
    },
    handleSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetchInquiries(), 400)
    },
    handleFilterChange() {
      this.fetchInquiries()
    },
    async fetchInquiries() {
      this.loading = true
      this.error = ''
      try {
        const res = await websiteContactService.list({
          search: this.searchQuery || undefined,
          type: this.selectedType || undefined,
          status: this.selectedStatus || undefined,
          pageSize: 50,
        })
        this.inquiries = res?.data?.items || []
      } catch (e) {
        this.error = e?.response?.data?.message || '載入失敗'
      } finally {
        this.loading = false
      }
    },
    openEdit(item) {
      this.editing = item
      this.editForm = {
        status: item.status || 'new',
        adminNote: item.admin_note || '',
      }
    },
    async saveEdit() {
      this.saving = true
      try {
        await websiteContactService.update(this.editing.id, {
          status: this.editForm.status,
          adminNote: this.editForm.adminNote,
        })
        showSuccess('已更新')
        this.editing = null
        this.fetchInquiries()
      } catch (e) {
        showError(e?.response?.data?.message || '更新失敗')
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      const ok = await showConfirm('確定刪除？', `${item.company}（${item.name}）`)
      if (!ok) return
      try {
        await websiteContactService.remove(item.id)
        showSuccess('已刪除')
        this.fetchInquiries()
      } catch (e) {
        showError(e?.response?.data?.message || '刪除失敗')
      }
    },
  },
}
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
.table-wrapper table { min-width: 1100px; }
.col-time { white-space: nowrap; font-size: 0.85rem; color: #64748b; }
.col-message { max-width: 200px; font-size: 0.875rem; color: #64748b; }
.btn-action { border: none; background: transparent; cursor: pointer; color: #0d9488; font-size: 18px; margin-right: 4px; }
.btn-action.danger { color: #dc2626; }
.badge { padding: 2px 8px; border-radius: 999px; font-size: 12px; background: #e2e8f0; }
.badge-success { background: #d1fae5; color: #065f46; }
.badge-warning { background: #fef3c7; color: #92400e; }
.badge-info { background: #e0f2fe; color: #075985; }
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px;
}
.modal-panel {
  background: #fff; border-radius: 12px; padding: 24px; width: min(560px, 100%);
  max-height: 90vh; overflow: auto;
}
.modal-panel h2 { margin: 0 0 4px; font-size: 1.1rem; }
.meta { color: #64748b; margin: 0 0 16px; font-size: 0.9rem; }
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-weight: 600; font-size: 13px; margin-bottom: 4px; }
.readonly { margin: 0; white-space: pre-wrap; color: #475569; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
</style>
